import { Link } from "react-router-dom";
import { BASIC_INTRO_LABEL } from "../../utils/basicPlanOffer";
import { categoryTitle } from "./DealRow";
import { HF } from "./homeFeedUi";

const INK = "#170B26";
const GOLD = "#FFD24A";

/**
 * Desktop left rail on a store page (Instacart-style aisles): the store and its deal count,
 * every department with its count, and the in-store clearance pitch.
 */
export function StoreRail({
  label,
  totalDeals,
  categories,
  onCategory,
  instore,
  top = 132,
  onChangeStore,
}: {
  top?: number;
  /** Opens the store picker under the given element. */
  onChangeStore?: (anchor: HTMLElement) => void;
  label: string;
  totalDeals: number;
  categories: { id: string; count: number }[];
  onCategory: (id: string) => void;
  instore: { title: string; eyebrow: string; to: string; onClick: () => void };
}) {
  return (
    <aside
      className="sticky hidden w-[232px] flex-shrink-0 flex-col gap-5 self-start overflow-y-auto overscroll-contain border-r pb-6 pl-4 pr-3 pt-6 [scrollbar-width:thin] lg:flex"
      style={{ top, height: `calc(100vh - ${top}px)`, borderColor: HF.border }}
      data-testid="store-rail"
    >
      <div className="flex flex-col gap-1 border-b px-2 pb-5" style={{ borderColor: HF.border }}>
        <span className="text-[26px] font-extrabold leading-tight tracking-[-0.6px]">{label}</span>
        {totalDeals > 0 && <span className="text-[14px]" style={{ color: HF.text3 }}>{totalDeals.toLocaleString()} deals online</span>}
        {onChangeStore && (
          <button
            type="button"
            onClick={(event) => onChangeStore(event.currentTarget)}
            aria-haspopup="dialog"
            className="mt-1 self-start text-[13.5px] font-semibold underline-offset-2 hover:underline"
            style={{ color: HF.store }}
          >
            Change store
          </button>
        )}
      </div>
      <nav aria-label={`${label} departments`} className="flex flex-col gap-0.5">
        <span className="px-2 pb-2 text-[16px] font-extrabold">Aisles</span>
        <span
          className="flex items-center justify-between rounded-[12px] px-3 py-2.5 text-[15px] font-bold"
          style={{ background: HF.inkBtn, color: HF.onInkBtn }}
          aria-current="page"
        >
          All deals
          {totalDeals > 0 && <span className="text-[13px] font-medium opacity-70">{totalDeals.toLocaleString()}</span>}
        </span>
        {categories.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onCategory(item.id)}
            className="flex items-center justify-between rounded-[12px] px-3 py-2.5 text-left text-[15px] font-semibold transition-colors hover:bg-[var(--hf-hover-strong)]"
            style={{ color: HF.ink }}
          >
            <span className="truncate">{categoryTitle(item.id)}</span>
            <span className="ml-2 flex-shrink-0 text-[13px] font-medium" style={{ color: HF.muted }}>{item.count.toLocaleString()}</span>
          </button>
        ))}
      </nav>
      <InstoreRailCard {...instore} />
    </aside>
  );
}

/** Dark "in store near you" pitch at the bottom of a store's left rail. */
export function InstoreRailCard({ title, eyebrow, to, onClick, compact = false }: { title: string; eyebrow: string; to: string; onClick: () => void; compact?: boolean }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex flex-col no-underline ${compact ? "gap-1.5 rounded-[16px] p-4" : "gap-2 rounded-[18px] p-[18px]"}`}
      style={{ background: INK, color: "#FFFFFF" }}
      data-testid="store-rail-instore"
    >
      <span className="text-[11.5px] font-extrabold tracking-[0.1em]" style={{ color: GOLD }}>{eyebrow}</span>
      <span className={`font-extrabold ${compact ? "text-[15px] leading-[20px]" : "text-[17px] leading-[22px]"}`}>{title}</span>
      {!compact && <span className="text-[13px] leading-[19px]" style={{ color: "#C9BEDB" }}>Members see the price, aisle and how many are left.</span>}
      <span className="mt-1 self-start rounded-full px-3.5 py-2 text-[13.5px] font-extrabold" style={{ background: GOLD, color: INK }}>
        {BASIC_INTRO_LABEL} first month
      </span>
    </Link>
  );
}

const STATUS_FILTERS = [
  { status: "new", key: "new", label: "New today" },
  { status: "price_drop", key: "priceDrop", label: "Price drops" },
  { status: "lowest", key: "lowest", label: "Lowest price yet" },
] as const;

/**
 * Quick filters above a store's deals. On the sectioned store page every chip opens the full
 * grid filtered to it; on the grid they switch the filter in place (`active` is the current one).
 */
export function StoreStatusChips({
  counts,
  active = "all",
  onStatus,
}: {
  counts?: Partial<Record<"new" | "priceDrop" | "lowest", number>>;
  active?: "all" | "new" | "price_drop" | "lowest";
  onStatus: (status: "all" | "new" | "price_drop" | "lowest") => void;
}) {
  const chip = (selected: boolean) =>
    selected
      ? { background: HF.inkBtn, color: HF.onInkBtn, fontWeight: 700 }
      : { background: HF.surface2, color: HF.ink, fontWeight: 600 };
  return (
    <div className="scrollbar-hide -mx-3 flex gap-2 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-1" data-testid="store-status-chips">
      <button
        type="button"
        onClick={() => onStatus("all")}
        aria-pressed={active === "all"}
        className="flex h-9 flex-shrink-0 items-center rounded-full px-4 text-[14px] transition-colors hover:opacity-90"
        style={chip(active === "all")}
      >
        All deals
      </button>
      {STATUS_FILTERS.map((filter) => {
        const count = counts?.[filter.key] || 0;
        const selected = active === filter.status;
        if (!count && !selected) return null;
        return (
          <button
            key={filter.status}
            type="button"
            onClick={() => onStatus(selected ? "all" : filter.status)}
            aria-pressed={selected}
            className="flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-[14px] transition-colors hover:opacity-90"
            style={chip(selected)}
          >
            {filter.label}
            {count > 0 && <span style={{ opacity: selected ? 0.7 : 1, color: selected ? undefined : HF.muted }}>{count.toLocaleString()}</span>}
          </button>
        );
      })}
    </div>
  );
}
